import { useCallback, useEffect, useState } from 'react';
import { CLIPS_TYPE, VOICEOVER_TYPE } from '../constants/clipsConstants';

const INIT_ACTION = {
  type: 'INIT',
};

export default function useSubtitlesActions({ updateSubtitles, changeLanguage, seekToSubtitle }) {
  const [actionsStorage, setActionsStorage] = useState([INIT_ACTION]);
  const [currentActionIndex, setCurrentActionIndex] = useState(0);
  const [controlsDisabled, setControlsDisabled] = useState({ undo: false, redo: false });

  const registerNewAction = useCallback(
    action => {
      setActionsStorage(prevState => {
        if (currentActionIndex < prevState.length - 1) {
          return currentActionIndex === 0 ? [INIT_ACTION, action] : [...prevState.slice(0, currentActionIndex + 1), action];
        } else {
          return [...prevState, action];
        }
      });
      setCurrentActionIndex(prevState => prevState + 1);
    },
    [currentActionIndex, setActionsStorage]
  );

  const newGeneralAction = useCallback(
    ({ updatedSceneSubtitles, prevSceneSubtitles, currentLanguage, sceneId = null, segmentId = null }) => {
      registerNewAction({ type: 'GENERAL', updatedSceneSubtitles, prevSceneSubtitles, currentLanguage, sceneId, segmentId });
    },
    [registerNewAction]
  );

  const undoLastAction = () => {
    if (currentActionIndex >= 1) {
      const currentAction = actionsStorage[currentActionIndex];
      if (currentAction?.type) {
        switch (currentAction.type) {
          case 'GENERAL':
            const { prevSceneSubtitles, currentLanguage, sceneId, segmentId, undoStartValue } = currentAction;
            const mode = sceneId ? CLIPS_TYPE : VOICEOVER_TYPE;
            updateSubtitles(prevState => ({
              ...prevState,
              [currentLanguage.id]: {
                ...prevState[currentLanguage.id],
                [mode]: prevState[currentLanguage.id][mode].map(item =>
                  item?.sceneId === sceneId || item?.segmentId === segmentId ? { ...item, subtitles: prevSceneSubtitles } : item
                ),
              },
            }));
            changeLanguage(currentLanguage);

            if (undoStartValue || undoStartValue === 0) {
              seekToSubtitle((undoStartValue + 50) / 1000);
            }
            break;
          default:
            break;
        }
      }
      setCurrentActionIndex(prevState => prevState - 1);
    }
  };

  const redoLastAction = () => {
    if (currentActionIndex < actionsStorage.length - 1) {
      const currentAction = actionsStorage[currentActionIndex + 1];
      switch (currentAction.type) {
        case 'GENERAL':
          const { updatedSceneSubtitles, currentLanguage, sceneId, segmentId, redoStartValue } = currentAction;
          const mode = sceneId ? CLIPS_TYPE : VOICEOVER_TYPE;
          updateSubtitles(prevState => ({
            ...prevState,
            [currentLanguage.id]: {
              ...prevState[currentLanguage.id],
              [mode]: prevState[currentLanguage.id][mode].map(item =>
                item?.sceneId === sceneId || item?.segmentId === segmentId ? { ...item, subtitles: updatedSceneSubtitles } : item
              )
            },
          }));
          changeLanguage(currentLanguage);

          if (redoStartValue || redoStartValue === 0) {
            seekToSubtitle((redoStartValue + 50) / 1000);
          }
          break;
        default:
          break;
      }
      setCurrentActionIndex(prevState => prevState + 1);
    }
  };

  const clearStorage = useCallback(() => {
    setActionsStorage([INIT_ACTION]);
    setCurrentActionIndex(0);
    setControlsDisabled({ undo: true, redo: true });
  }, []);

  useEffect(() => {
    setControlsDisabled({
      undo: currentActionIndex === 0,
      redo: currentActionIndex >= actionsStorage.length - 1 || actionsStorage.length === 1,
    });
  }, [currentActionIndex]);

  return {
    newGeneralAction,
    undoLastAction,
    redoLastAction,
    clearStorage,
    currentActionIndex,
    controlsDisabled,
  };
}
